import { BucketBadge } from './BucketBadge';
import { ClauseSummary } from './ClauseSummary';
import { LABELS, formatValue } from './ExtractionReview';
import { Screen } from '../../components/lease/Screen';
import { BrandHeading } from '../../components/lease/BrandHeading';
import { PrimaryButton } from '../../components/lease/PrimaryButton';
import { PAYMENTS_ENABLED } from '../../config';

// An omission finding flags what the lease is MISSING (required_absence rule):
// there is no clause to quote, so section_id is null and matched_text empty.
// Prefers the backend-computed `kind` (single source of truth, Task 3); falls
// back to the section_id/matched_text heuristic only for reports stored
// before `kind` was serialized.
const isOmission = (f) =>
  'kind' in f ? f.kind === 'omission' : f.section_id == null && !f.matched_text;

// Overall band -> one of the 4 severity colors (separate from the emerald
// brand accent, per the design standard's semantic-color requirement).
function bandStyle(band = '') {
  const b = band.toLowerCase();
  if (b.includes('high')) return { className: 'ring-red-200 bg-red-50 dark:ring-red-900 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400' };
  if (b.includes('moderate') || b.includes('medium')) return { className: 'ring-orange-200 bg-orange-50 dark:ring-orange-900 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-400' };
  if (b.includes('low')) return { className: 'ring-emerald-200 bg-emerald-50 dark:ring-emerald-900 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400' };
  return { className: 'ring-amber-200 bg-amber-50 dark:ring-amber-900 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-400' };
}

function Finding({ f, ruledOut = false, onWriteRequest }) {
  const bucketClass = ruledOut ? '' : bucketRing(f);
  return (
    <div
      className={`rounded-xl p-4 ring-1 backdrop-blur ${
        ruledOut
          ? 'bg-white/50 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700'
          : `${bucketClass} bg-white/70 dark:bg-slate-800/60`
      }`}
    >
      <div className="flex items-center gap-2">
        {ruledOut ? (
          <span className="inline-block flex-none rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
            Checked — not an issue
          </span>
        ) : (
          <BucketBadge finding={f} />
        )}
        <h4 className={`font-semibold ${ruledOut ? 'text-slate-600 dark:text-slate-300' : 'text-slate-800 dark:text-slate-100'}`}>
          {f.title}
        </h4>
      </div>
      {ruledOut && f.concern && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{f.concern}</p>}
      {f.matched_text && (
        <blockquote className="mt-2 text-sm italic text-slate-600 dark:text-slate-300">“{f.matched_text}”</blockquote>
      )}
      {f.section_ref && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Found in: {f.section_ref}</p>
      )}
      {!ruledOut && f.concern && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{f.concern}</p>}
      {f.citation && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{f.citation}</p>}
      {!ruledOut && f.recommendation && (
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
          <strong className="text-emerald-700 dark:text-emerald-400">Next step:</strong> {f.recommendation}
        </p>
      )}
      {!ruledOut && f.tier === 'violation' && (
        <p className="mt-2 text-xs font-semibold text-red-700 dark:text-red-400">
          ⚠ We recommend attorney review of this item.
        </p>
      )}
      {!ruledOut && onWriteRequest && (
        <button
          type="button"
          onClick={() => onWriteRequest(f)}
          className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60"
        >
          Create written request →
        </button>
      )}
    </div>
  );
}

// Same severity tint used by BucketBadge's chip, but as a soft ring/bg for the card.
function bucketRing(f) {
  if (f.tier === 'violation' && f.risk_level === 'high') return 'ring-red-200 dark:ring-red-900';
  if (f.risk_level === 'high' || f.tier === 'violation') return 'ring-orange-200 dark:ring-orange-900';
  if (f.confidence !== undefined && f.confidence < 0.6) return 'ring-amber-200 dark:ring-amber-900';
  return 'ring-orange-200 dark:ring-orange-900';
}

function Section({ title, description, children }) {
  return (
    <section className="mt-8 animate-slide-in">
      <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{title}</h3>
      {description && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{description}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

const asIssue = (f) => ({
  rule_id: f.rule_id ?? null,
  title: f.title,
  section_ref: f.section_ref,
  matched_text: f.matched_text,
  recommendation: f.recommendation,
});

function LeaseGlance({ details }) {
  // "Lease at a glance" (Bar 1): the submitted document's own parties,
  // property, dates, and amounts — shown before any findings so the report
  // mirrors the lease. Null/empty details -> render nothing.
  const rows = Object.entries(details || {}).filter(([, v]) => v !== null && v !== undefined);
  if (rows.length === 0) return null;
  return (
    <section className="mt-6 animate-slide-in rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
      <h2 className="font-bold text-emerald-800 dark:text-emerald-300">Lease at a glance</h2>
      <dl className="mt-2 divide-y divide-emerald-100 dark:divide-slate-700">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 py-2 first:pt-0 last:pb-0">
            <dt className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">{LABELS[k] || k}</dt>
            <dd className="text-right text-sm text-slate-700 dark:text-slate-200">{formatValue(k, v)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function RiskReport({ report, details, onDownload, onSaveReport, onFindAttorney, onWriteRequest }) {
  const violations = report.violations || [];
  const advisories = report.advisories || [];
  const ruledOut = report.possible_issues || [];
  // Every active finding is itemizable in a written request; the per-finding
  // CTA pre-checks just that finding, the report-level button checks all.
  const activeFindings = [...violations, ...advisories];
  const writeRequest = onWriteRequest
    ? (checked) => onWriteRequest({ issues: activeFindings.map(asIssue), checked })
    : null;
  const writeOne = writeRequest
    ? (f) => writeRequest([activeFindings.indexOf(f)])
    : null;
  const omissions = advisories.filter(isOmission);
  const clauseAdvisories = advisories.filter((f) => !isOmission(f));
  // Ruled-out checks are shown for transparency but never feed the questions.
  const questions = [...violations, ...advisories]
    .filter((f) => f.recommendation)
    .map((f) => f.recommendation);
  const drivenBy = violations.map((f) => f.title).join('; ');
  const band = bandStyle(report.band);

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">Risk Report</BrandHeading>
      </header>

      <LeaseGlance details={details} />

      <div
        className={`mt-6 animate-slide-in rounded-2xl p-5 text-center shadow-md ring-1 backdrop-blur ${band.className}`}
      >
        {/* Consumer-facing signal is the band (bucket), never the 0-100 score
            — the numeric score is internal-only per the locked design. The
            backend `summary` string bakes the score in ("Lease health 63/100…"),
            so we compose the headline from the finding counts instead. */}
        <p className={`text-lg font-bold ${band.text}`}>{report.band}</p>
        <p className="mt-2 text-slate-700 dark:text-slate-200">
          {report.violation_count} issue{report.violation_count === 1 ? '' : 's'} to address
          {report.advisory_count
            ? `, ${report.advisory_count} advisory item${report.advisory_count === 1 ? '' : 's'}`
            : ''}
          .
        </p>
        {report.rules_checked && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Screened against {report.rules_checked} Colorado residential-lease rules.
          </p>
        )}
        {report.refer_to_attorney && (
          <p className="mt-2 font-semibold text-red-700 dark:text-red-400">
            We recommend attorney review of this lease
            {drivenBy ? ` — driven by: ${drivenBy}.` : '.'}
          </p>
        )}
      </div>

      <Section title="Top issues before signing">
        {violations.map((f, i) => (
          <Finding key={i} f={f} onWriteRequest={writeOne} />
        ))}
      </Section>

      {omissions.length > 0 && (
        <Section
          title="What your lease doesn't tell you"
          description="Disclosures or protections Colorado law expects that we could not find in your lease."
        >
          {omissions.map((f, i) => (
            <Finding key={i} f={f} onWriteRequest={writeOne} />
          ))}
        </Section>
      )}

      <Section title="Watch-outs & advisories">
        {clauseAdvisories.map((f, i) => (
          <Finding key={i} f={f} onWriteRequest={writeOne} />
        ))}
      </Section>

      {/* Always rendered: the thorough-review trust signal — an empty state
          still says the screening happened. */}
      <Section
        title="We checked these — they appear not to apply"
        description="Clauses we screened that turned out fine on a closer read; shown for transparency. They do not affect your score."
      >
        {ruledOut.length ? (
          ruledOut.map((f, i) => <Finding key={i} f={f} ruledOut />)
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Every rule check came back clean or is reported above — nothing else needed a closer look.
          </p>
        )}
      </Section>

      <ClauseSummary entries={report.clause_summary} />

      <Section title="Questions to ask the landlord">
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-200">
          {questions.length ? (
            questions.map((q, i) => <li key={i}>{q}</li>)
          ) : (
            <li>No specific questions — review the flagged clauses above.</li>
          )}
        </ul>
      </Section>

      {PAYMENTS_ENABLED && writeRequest && activeFindings.length > 0 && (
        <button
          onClick={() => writeRequest(activeFindings.map((_, i) => i))}
          className="mt-8 w-full animate-slide-in rounded-xl bg-emerald-600 py-4 text-lg font-bold text-white transition hover:bg-emerald-700"
        >
          Put it in writing — ask about {activeFindings.length === 1 ? 'this item' : `all ${activeFindings.length} items`}
        </button>
      )}

      <PrimaryButton onClick={onDownload} className="mt-3 animate-slide-in">
        Download PDF
      </PrimaryButton>

      {onSaveReport && (
        <button
          onClick={onSaveReport}
          className="mt-3 w-full animate-slide-in rounded-xl bg-white py-4 text-lg font-bold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-50 dark:bg-slate-800/60 dark:text-emerald-400 dark:ring-slate-700"
        >
          Save Report
        </button>
      )}

      {onFindAttorney && (
        <button
          onClick={onFindAttorney}
          className="mt-3 w-full animate-slide-in rounded-xl bg-white py-4 text-lg font-bold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-50 dark:bg-slate-800/60 dark:text-emerald-400 dark:ring-slate-700"
        >
          Talk to a local attorney
        </button>
      )}

      <footer className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
        This report screens your Colorado residential lease for common legal and financial
        risks. It is <strong>not legal advice</strong>. For a legal opinion, consult a
        licensed Colorado attorney.
      </footer>
    </Screen>
  );
}
